import React, { useState, useEffect } from 'react';
import { FolderOpen, Plus, Search, Edit, Trash2, Eye, MoreVertical, Clock, Users, FileText, Tag, Filter, BarChart3 } from 'lucide-react';
import { superAdminAPI } from '../../services/api';
import { CardLoadingSpinner } from '../ui/LoadingSpinner';
import { showSuccessToast, showErrorToast } from '../../utils/errorHandling';
import KebabMenu from '../ui/KebabMenu';
import CreateKitModal from './CreateKitModal';
import EditKitModal from './EditKitModal';
import ViewKitModal from './ViewKitModal';
import TagMappingModal from './TagMappingModal';

const KitManagement = () => {
  const [kits, setKits] = useState([]);
  const [filteredKits, setFilteredKits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedKits, setSelectedKits] = useState([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isTagMappingModalOpen, setIsTagMappingModalOpen] = useState(false);
  const [selectedKit, setSelectedKit] = useState(null);

  // Statistics
  const [stats, setStats] = useState({
    totalKits: 0,
    activeKits: 0,
    draftKits: 0,
    totalQuestions: 0
  });

  useEffect(() => {
    loadKits();
  }, []);

  useEffect(() => {
    filterKits();
  }, [kits, searchTerm, typeFilter, statusFilter]);

  const loadKits = async () => {
    try {
      setLoading(true);
      const response = await superAdminAPI.getAllKits();
      // Handle paginated response
      const kitData = response.data?.content || response.data || [];
      setKits(kitData);

      // Calculate statistics
      const stats = {
        totalKits: kitData.length,
        activeKits: kitData.filter(kit => kit.status === 'PUBLISHED').length,
        draftKits: kitData.filter(kit => kit.status === 'DRAFT').length,
        totalQuestions: kitData.reduce((sum, kit) => sum + (kit.questionCount || 0), 0)
      };
      setStats(stats);
    } catch (error) {
      console.error('Error loading kits:', error);
      showErrorToast(error, 'Failed to load KITs');
      // Set empty data on error
      setKits([]);
      setStats({
        totalKits: 0,
        activeKits: 0,
        draftKits: 0,
        totalQuestions: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const filterKits = () => {
    let filtered = [...kits];

    if (searchTerm) {
      filtered = filtered.filter(kit =>
        kit.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        kit.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        kit.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    if (typeFilter) {
      filtered = filtered.filter(kit => kit.type === typeFilter);
    }

    if (statusFilter) {
      filtered = filtered.filter(kit => kit.status === statusFilter);
    }

    setFilteredKits(filtered);
  };

  const handleCreateKit = () => {
    setSelectedKit(null);
    setIsCreateModalOpen(true);
  };

  const handleEditKit = (kit) => {
    setSelectedKit(kit);
    setIsEditModalOpen(true);
  };

  const handleDeleteKit = async (kit) => {
    if (window.confirm(`Are you sure you want to delete the KIT "${kit.name}"?`)) {
      try {
        await superAdminAPI.deleteKit(kit.id);
        showSuccessToast('KIT deleted successfully!');
        loadKits();
      } catch (error) {
        console.error('Error deleting kit:', error);
        showErrorToast(error, 'Failed to delete KIT');
      }
    }
  };

  const handleViewKit = (kit) => {
    setSelectedKit(kit);
    setIsViewModalOpen(true);
  };

  const handleKitCreated = () => {
    setIsCreateModalOpen(false);
    loadKits();
    showSuccessToast('KIT created successfully!');
  };

  const handleKitUpdated = () => {
    setIsEditModalOpen(false);
    setSelectedKit(null);
    loadKits();
    showSuccessToast('KIT updated successfully!');
  };

  const handleManageTags = (kit) => {
    setSelectedKit(kit);
    setIsTagMappingModalOpen(true);
  };

  const handleTagsUpdated = () => {
    setIsTagMappingModalOpen(false);
    setSelectedKit(null);
    loadKits();
    showSuccessToast('Kit tags updated successfully!');
  };

  const getKitTypeColor = (type) => {
    const colors = {
      'YEARLY': 'bg-blue-100 text-blue-800',
      'REAL_EXAM': 'bg-green-100 text-green-800',
      'FUNDAMENTALS': 'bg-purple-100 text-purple-800',
      'ADVANCED': 'bg-red-100 text-red-800',
      'MIXED': 'bg-gray-100 text-gray-800',
      'PRACTICE': 'bg-yellow-100 text-yellow-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const getStatusColor = (status) => {
    const colors = {
      'DRAFT': 'bg-gray-100 text-gray-800',
      'PUBLISHED': 'bg-green-100 text-green-800',
      'ARCHIVED': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return <CardLoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Kit Management</h1>
          <p className="text-gray-600">Manage question kits and their tag mappings</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handleCreateKit}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Kit
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <FolderOpen className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Kits</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalKits}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <BarChart3 className="h-8 w-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Active Kits</p>
              <p className="text-2xl font-bold text-gray-900">{stats.activeKits}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <Edit className="h-8 w-8 text-yellow-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Draft Kits</p>
              <p className="text-2xl font-bold text-gray-900">{stats.draftKits}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <Users className="h-8 w-8 text-purple-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Questions</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalQuestions}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search kits..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Types</option>
            <option value="YEARLY">Yearly</option>
            <option value="REAL_EXAM">Real Exam</option>
            <option value="FUNDAMENTALS">Fundamentals</option>
            <option value="ADVANCED">Advanced</option>
            <option value="MIXED">Mixed</option>
            <option value="PRACTICE">Practice</option>
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Status</option>
            <option value="DRAFT">Draft</option>
            <option value="PUBLISHED">Published</option>
            <option value="ARCHIVED">Archived</option>
          </select>

          <div className="flex items-center text-sm text-gray-600">
            <Filter className="h-4 w-4 mr-2" />
            {filteredKits.length} of {kits.length} kits
          </div>
        </div>
      </div>

      {/* Kits Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Kit Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type & Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tags
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Questions
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredKits.map((kit) => (
                <tr key={kit.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{kit.name}</div>
                      <div className="text-sm text-gray-500 max-w-xs truncate">
                        {kit.description || 'No description'}
                      </div>
                      {kit.subject && (
                        <div className="text-xs text-blue-600 mt-1">{kit.subject}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-1">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getKitTypeColor(kit.type)}`}>
                        {kit.type || 'MIXED'}
                      </span>
                      <br />
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(kit.status)}`}>
                        {kit.status || 'DRAFT'}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1 max-w-xs">
                      {kit.tags && kit.tags.length > 0 ? (
                        kit.tags.slice(0, 3).map((tag, index) => (
                          <span key={index} className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-800">
                            <Tag className="h-3 w-3 mr-1" />
                            {tag}
                          </span>
                        ))
                      ) : (
                        <span className="text-xs text-gray-500">No tags</span>
                      )}
                      {kit.tags && kit.tags.length > 3 && (
                        <span className="text-xs text-gray-500">+{kit.tags.length - 3} more</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {kit.questionCount || 0}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDate(kit.createdAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <KebabMenu
                      items={[
                        { label: 'View Details', icon: Eye, onClick: () => handleViewKit(kit) },
                        { label: 'Edit Kit', icon: Edit, onClick: () => handleEditKit(kit) },
                        { label: 'Manage Tags', icon: Tag, onClick: () => handleManageTags(kit) },
                        { label: 'Delete Kit', icon: Trash2, onClick: () => handleDeleteKit(kit), className: 'text-red-600' }
                      ]}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredKits.length === 0 && (
          <div className="text-center py-12">
            <FolderOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No kits found</h3>
            <p className="text-gray-500 mb-4">
              {searchTerm || typeFilter || statusFilter
                ? 'Try adjusting your filters to see more results.'
                : 'Get started by creating your first kit.'}
            </p>
            {!searchTerm && !typeFilter && !statusFilter && (
              <button
                onClick={handleCreateKit}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center mx-auto"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create First Kit
              </button>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      {isCreateModalOpen && (
        <CreateKitModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onKitCreated={handleKitCreated}
        />
      )}

      {isEditModalOpen && selectedKit && (
        <EditKitModal
          isOpen={isEditModalOpen}
          kit={selectedKit}
          onClose={() => setIsEditModalOpen(false)}
          onKitUpdated={handleKitUpdated}
        />
      )}

      {isViewModalOpen && selectedKit && (
        <ViewKitModal
          isOpen={isViewModalOpen}
          kit={selectedKit}
          onClose={() => setIsViewModalOpen(false)}
        />
      )}

      {isTagMappingModalOpen && selectedKit && (
        <TagMappingModal
          isOpen={isTagMappingModalOpen}
          kit={selectedKit}
          onClose={() => setIsTagMappingModalOpen(false)}
          onTagsUpdated={handleTagsUpdated}
        />
      )}
    </div>
  );
};

export default KitManagement;
